// =====================================================================
// Cart server sync — module-level subscription. Registers once.
// After auth is ready: fetches server cart and merges into store.
// On every store change: debounces (500ms) a server save.
// Server unavailable → silent fallback (local-only works fine).
// =====================================================================

import { useCartStore } from "./store";
import { fetchCartFromServer, saveCartToServer } from "../api";
import { useTelegramStore } from "@/shared/store/telegram";

let _registered = false;
let _debounceTimer: ReturnType<typeof setTimeout> | null = null;

function subscribe(): void {
  useCartStore.subscribe((_state) => {
    if (_debounceTimer) clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(() => {
      const initData = useTelegramStore.getState().initData;
      if (initData.length === 0) return;
      void saveCartToServer(initData, useCartStore.getState().items);
    }, 500);
  });
}

function pollInitData(): void {
  const tryFetch = (): void => {
    const initData = useTelegramStore.getState().initData;
    if (initData.length === 0) return;
    void fetchCartFromServer(initData).then((serverItems) => {
      if (serverItems.length === 0) return;
      const s = useCartStore.getState();
      if (!s.hydrated) {
        // Cart not yet hydrated — the server items will be picked
        // up by `hydrate()` because we write them to the store here
        // and `hydrate()` only overwrites if its loaded array is
        // non-empty. Since we just wrote, hydrate sees `items` is
        // non-empty and skips the local load.
      }
      useCartStore.setState({ items: serverItems });
    });
  };

  // Check every 200ms for up to 10s
  let attempts = 0;
  const iv = setInterval(() => {
    attempts++;
    const initData = useTelegramStore.getState().initData;
    if (initData.length > 0) {
      clearInterval(iv);
      tryFetch();
      return;
    }
    if (attempts >= 50) clearInterval(iv); // 10s timeout
  }, 200);
}

export function initCartServerSync(): void {
  if (_registered) return;
  _registered = true;

  // Register the debounced-save subscription immediately
  subscribe();

  // Poll for initData to be available, then try server fetch
  if (typeof window !== "undefined") {
    pollInitData();
  }
}
